// src/components/backoffice/articles/CategoriesPage.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { articlesService, ArticleCategory } from '../../../services/backoffice/articlesService';

const CategoriesPage: React.FC = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<ArticleCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // טופס
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formName, setFormName] = useState('');
  const [formIcon, setFormIcon] = useState('file-text');
  const [formSortOrder, setFormSortOrder] = useState('0');
  const [formIsActive, setFormIsActive] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const loadCategories = useCallback(async () => {
    try {
      setLoading(true);
      const data = await articlesService.getCategories();
      setCategories(data);
    } catch (err: any) {
      setError(`שגיאה בטעינת קטגוריות: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  useEffect(() => {
    if (successMessage) {
      const t = setTimeout(() => setSuccessMessage(''), 4000);
      return () => clearTimeout(t);
    }
  }, [successMessage]);

  const resetForm = () => {
    setEditingId(null);
    setFormName('');
    setFormIcon('file-text');
    setFormSortOrder('0');
    setFormIsActive(true);
    setShowForm(false);
  };

  const handleEdit = (cat: ArticleCategory) => {
    setEditingId(cat.id);
    setFormName(cat.name);
    setFormIcon(cat.icon || 'file-text');
    setFormSortOrder(String(cat.sort_order || 0));
    setFormIsActive(!!cat.is_active);
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!formName.trim()) { setError('שם הקטגוריה הוא שדה חובה'); return; }

    try {
      setSaving(true);
      setError('');

      const payload = {
        name: formName,
        icon: formIcon,
        sort_order: formSortOrder,
        is_active: formIsActive ? '1' : '0',
        ...(editingId ? { id: String(editingId) } : {}),
      };

      if (editingId) {
        await articlesService.updateCategory(payload);
        setSuccessMessage('הקטגוריה עודכנה בהצלחה');
      } else {
        await articlesService.createCategory(payload);
        setSuccessMessage('הקטגוריה נוצרה בהצלחה');
      }

      resetForm();
      await loadCategories();
    } catch (err: any) {
      setError(`שגיאה בשמירה: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto" dir="rtl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">ניהול קטגוריות</h1>
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/backoffice/articles')}
            className="text-gray-500 hover:text-gray-700 text-sm"
          >
            ← חזרה למאמרים
          </button>
          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            + קטגוריה חדשה
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
          {successMessage}
        </div>
      )}

      {/* טופס יצירה/עריכה */}
      {showForm && (
        <div className="bg-white rounded-xl shadow p-5 mb-6 border-r-4 border-blue-500">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            {editingId ? 'עריכת קטגוריה' : 'קטגוריה חדשה'}
          </h2>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="cat-name" className="block text-sm font-medium text-gray-700 mb-1">
                שם <span className="text-red-500">*</span>
                <span className="sr-only">(שדה חובה)</span>
              </label>
              <input
                id="cat-name"
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                aria-required="true"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label htmlFor="cat-icon" className="block text-sm font-medium text-gray-700 mb-1">
                אייקון
              </label>
              <input
                id="cat-icon"
                type="text"
                value={formIcon}
                onChange={(e) => setFormIcon(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="file-text"
              />
            </div>
            <div>
              <label htmlFor="cat-sort" className="block text-sm font-medium text-gray-700 mb-1">
                סדר תצוגה
              </label>
              <input
                id="cat-sort"
                type="number"
                value={formSortOrder}
                onChange={(e) => setFormSortOrder(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-end pb-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formIsActive}
                  onChange={(e) => setFormIsActive(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <span className="text-sm font-medium text-gray-700">פעיל</span>
              </label>
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <button onClick={resetForm} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
              ביטול
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'שומר...' : editingId ? 'שמור' : 'צור'}
            </button>
          </div>
        </div>
      )}

      {/* טבלת קטגוריות */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">טוען קטגוריות...</div>
      ) : categories.length === 0 ? (
        <div className="text-center py-12 text-gray-500">אין קטגוריות עדיין</div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-right px-4 py-3 font-medium text-gray-600">שם</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">מאמרים</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">סדר</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">סטטוס</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {categories.map((cat) => (
                <tr key={cat.id} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-800">{cat.name}</td>
                  <td className="px-4 py-3 text-gray-600">{cat.articles_count ?? 0}</td>
                  <td className="px-4 py-3 text-gray-600">{cat.sort_order}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                      cat.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {cat.is_active ? 'פעיל' : 'לא פעיל'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-left">
                    <button
                      onClick={() => handleEdit(cat)}
                      className="px-3 py-1 text-blue-600 border border-blue-200 rounded hover:bg-blue-50 transition-colors"
                    >
                      עריכה
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CategoriesPage;